import { useIntl } from 'react-intl';

import type { SocialUrlsData } from '@xing-com/crate-jobs-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import {
  IconButterflyMono,
  IconCompaniesFilled,
  IconFacebook,
  IconInstagram,
  IconKununu,
  IconPinterest,
  IconTiktok,
  IconTwitter,
  IconYoutube,
} from '@xing-com/icons';

import { SocialLinkButton, SocialLinksWrapper } from './social-links.styles';

type Service = keyof SocialUrlsData;

type SocialLinkProps = {
  jobId: string;
  url: string;
  service: Service;
};

type SocialLinksProps = {
  jobId: string;
  socialUrls: SocialUrlsData;
};

const SERVICE_ICON: Record<Service, React.FC> = {
  website: IconCompaniesFilled,
  facebook: IconFacebook,
  twitter: IconTwitter,
  youtube: IconYoutube,
  xing: IconButterflyMono,
  instagram: IconInstagram,
  pinterest: IconPinterest,
  tiktok: IconTiktok,
  kununu: IconKununu,
};

const SERVICE_LABEL: Record<Service, string> = {
  website: 'JOBS_JOB_DETAILS_DESCRIPTION_COMPANY_WEB_ICON_LINK',
  facebook: 'JOBS_JOB_DETAILS_DESCRIPTION_FACEBOOK_ICON_LINK',
  twitter: 'JOBS_JOB_DETAILS_DESCRIPTION_TWITTER_ICON_LINK',
  youtube: 'JOBS_JOB_DETAILS_DESCRIPTION_YOUTUBE_ICON_LINK',
  xing: 'JOBS_JOB_DETAILS_DESCRIPTION_XING_ICON_LINK',
  instagram: 'JOBS_JOB_DETAILS_DESCRIPTION_INSTAGRAM_ICON_LINK',
  pinterest: 'JOBS_JOB_DETAILS_DESCRIPTION_PINTEREST_ICON_LINK',
  tiktok: 'JOBS_JOB_DETAILS_DESCRIPTION_TIKTOK_ICON_LINK',
  kununu: 'JOBS_JOB_DETAILS_DESCRIPTION_KUNUNU_ICON_LINK',
};

const isSupportedService = (service: string): service is Service => {
  return Object.keys(SERVICE_ICON).includes(service);
};

const isValidLink = (
  entry: [string, string | null]
): entry is [Service, string] => isSupportedService(entry[0]) && !!entry[1];

const SocialLink: React.FC<SocialLinkProps> = ({ jobId, url, service }) => {
  const trackNwtEvent = useJobsNwtTracking();
  const { formatMessage } = useIntl();

  return (
    <SocialLinkButton
      icon={SERVICE_ICON[service]}
      href={url}
      target="_blank"
      rel="nofollow noopener"
      aria-label={formatMessage({ id: SERVICE_LABEL[service] })}
      onClick={(event) => {
        // Stop propagation so this event doesn't register as a description link
        // click event
        event.stopPropagation();

        trackNwtEvent({
          eventSchema: 'basic',
          page: 'jobs/detail',
          event: service === 'xing' ? 'opened' : 'visited',
          element: 'job_description_social_target',
          itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
        });
      }}
    />
  );
};

export const SocialLinks: React.FC<SocialLinksProps> = ({
  jobId,
  socialUrls,
}) => {
  const validLinks = Object.entries(socialUrls).filter(isValidLink);

  if (!validLinks.length) return null;

  return (
    <SocialLinksWrapper>
      {validLinks.map(([service, url]) => {
        return (
          <SocialLink key={service} jobId={jobId} service={service} url={url} />
        );
      })}
    </SocialLinksWrapper>
  );
};
